import React, { useState, useRef } from "react";
import { Card, Button, Form, Alert, Table, Badge, Spinner } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import productItemEanApi from "../../services/productItemEanApi";
import { CatalogHeader } from "../../components/catalog/CatalogUI";

const ProductItemEanBulkUpload = () => {
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState(null);

  const submitLockRef = useRef(false);

  const downloadSampleFile = () => {
    const csvContent = [
      "item_ref_code,ean_code,is_primary",
      "1000002,8901000000001,1",
      "1000944,8901000000002,1",
      "1003219,8901000000003,1",
      "1003231,8901000000004,1",
      "1000003,8901000000005,1",
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "sample_product_item_ean_upload.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (submitLockRef.current || loading) {
      return;
    }

    if (!file) {
      toast.error("Please choose an Excel/CSV file");
      return;
    }

    submitLockRef.current = true;

    const formData = new FormData();
    formData.append("file", file);

    try {
      setLoading(true);
      setSummary(null);

      const res = await productItemEanApi.importBulk(formData);

      if (res?.status === "SUCCESS") {
        toast.success(res.message || "EAN bulk upload completed");
        setSummary(res.data || null);
      } else {
        toast.error(res?.message || "EAN bulk upload failed");
      }
    } catch (err) {
      toast.error(
        err?.response?.data?.message ||
        err?.message ||
        "Failed to upload file"
      );
    } finally {
      setLoading(false);
      submitLockRef.current = false;
    }
  };

  return (
    <div className="catalog-page">
      <CatalogHeader title="Bulk EAN Upload" description="Upload barcode mappings, review import results and download the sample format." />
      <ToastContainer />
      <Card>
        <Card.Header className="d-flex justify-content-between align-items-center">
          <h4 className="mb-0">Product Item EAN Bulk Upload</h4>
          <div className="d-flex gap-2">
            <Button
              variant="outline-secondary"
              onClick={() => navigate("/admin/products-item-ean-codes")}
              disabled={loading}
            >
              Back to List
            </Button>
            <Button
              variant="outline-primary"
              onClick={downloadSampleFile}
              disabled={loading}
            >
              Download Sample File
            </Button>
          </div>
        </Card.Header>

        <Card.Body>
          <Alert variant="info">
            <div><strong>Accepted columns:</strong> item_ref_code, ean_code, is_primary</div>
            <div className="mt-1">
              Also supports headers like <code>Item Ref Code</code>, <code>EAN Code</code>, <code>Is Primary</code>.
            </div>
          </Alert>

          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>Select Excel / CSV File</Form.Label>
              <Form.Control
                type="file"
                accept=".xlsx,.xls,.csv"
                disabled={loading}
                onChange={(e) => setFile(e.target.files?.[0] || null)}
              />
            </Form.Group>

            <Button type="submit" variant="primary" disabled={loading || !file}>
              {loading ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Uploading...
                </>
              ) : (
                "Upload"
              )}
            </Button>
          </Form>

          {summary && (
            <Card className="mt-4">
              <Card.Header><strong>Upload Summary</strong></Card.Header>
              <Card.Body>
                <div className="row mb-3">
                  <div className="col-md-3">
                    <p><strong>Total Rows:</strong> {summary.totalRows}</p>
                  </div>
                  <div className="col-md-3">
                    <p><strong>Processed Rows:</strong> {summary.processedRows}</p>
                  </div>
                  <div className="col-md-3">
                    <p><strong>Inserted:</strong> <Badge bg="success">{summary.inserted}</Badge></p>
                  </div>
                  <div className="col-md-3">
                    <p><strong>Updated:</strong> <Badge bg="warning">{summary.updated}</Badge></p>
                  </div>
                </div>

                {summary.missingItems?.length > 0 && (
                  <>
                    <h6 className="mt-3 text-danger">Missing Product Items</h6>
                    <Table bordered responsive size="sm">
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Excel Row</th>
                          <th>Reference Code</th>
                          <th>EAN Code</th>
                        </tr>
                      </thead>
                      <tbody>
                        {summary.missingItems.map((item, idx) => (
                          <tr key={`${item.row}-${item.itemRefCode}-${idx}`}>
                            <td>{idx + 1}</td>
                            <td>{item.row}</td>
                            <td>{item.itemRefCode}</td>
                            <td>{item.eanCode}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </>
                )}

                {summary.duplicatesInFile?.length > 0 && (
                  <>
                    <h6 className="mt-3 text-warning">Duplicate Rows in File</h6>
                    <Table bordered responsive size="sm">
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Excel Row</th>
                          <th>Reference Code</th>
                          <th>EAN Code</th>
                        </tr>
                      </thead>
                      <tbody>
                        {summary.duplicatesInFile.map((item, idx) => (
                          <tr key={`${item.row}-${item.itemRefCode}-${idx}`}>
                            <td>{idx + 1}</td>
                            <td>{item.row}</td>
                            <td>{item.itemRefCode}</td>
                            <td>{item.eanCode}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </>
                )}

                {summary.errors?.length > 0 && (
                  <>
                    <h6 className="mt-3 text-danger">Errors</h6>
                    <Table bordered responsive size="sm">
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Excel Row</th>
                          <th>Message</th>
                        </tr>
                      </thead>
                      <tbody>
                        {summary.errors.map((err, idx) => (
                          <tr key={`${err.row || idx}-${idx}`}>
                            <td>{idx + 1}</td>
                            <td>{err.row || "-"}</td>
                            <td>{err.message}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </>
                )}

                {!summary.missingItems?.length &&
                  !summary.duplicatesInFile?.length &&
                  !summary.errors?.length && (
                    <Alert variant="success" className="mb-0">
                      All rows processed successfully.
                    </Alert>
                  )}
              </Card.Body>
            </Card>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

export default ProductItemEanBulkUpload;
